import { CheckIcon } from '@radix-ui/react-icons'
import { useRuntimeVariables } from 'app-kit'
import { Draggable } from 'components/Draggable'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { AnnotationLabelInput } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/AnnotationLabelInput'
import { AnnotationNotesInput } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/AnnotationNotesInput'
import { DamageTypeSelector } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/DamageTypeSelector'
import { SeverityPicker } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/SeverityPicker'
import { CloseButton } from 'features/kestrelViewer/ui/popupMenu/CloseButton'
import { findBestAnnotationImageFilename } from 'features/kestrelViewer/utils/pdfExportUtils'
import React, { FC, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { KVAnnotation } from 'types'

const fadeInPopup = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const PopupContainer = styled.div`
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  color: white;
  line-height: 1rem;
  width: 22rem;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
  opacity: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${fadeInPopup} 0.3s ease-out forwards;
`

const InputGroup = styled.div`
  margin-bottom: 0.5rem;
  text-align: left;
`

const InputHeader = styled.div`
  font-size: 0.65rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`

const CenteredContent = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #666666;
  background-color: #000000;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  flex: 1;

  &:hover {
    background-color: #444444;
    border-color: #888888;
  }
`

const SeveritySavePhotoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const SeveritySection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const SavePhotoSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`

const CheckboxContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SavePhotoCheckbox = styled.input`
  width: 30px;
  height: 30px;
  border: 1px solid #666666;
  background-color: #000000;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background-color: #444444;
    border-color: #888888;
  }

  &:checked {
    background-color: #2e7d32;
    border-color: #4caf50;
  }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
`

const DraggableHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.6);
`

export const AnnotationMenu: FC<{
  selectedAnnotation: KVAnnotation
}> = ({ selectedAnnotation }) => {
  const { updateAnnotation, setSelectedMarkup, popupMenuStartPosition } =
    useKestrelViewerContext()
  const { kestrelModel, authorizedAxios } = useKestrel()
  const { KESTREL_API_URL } = useRuntimeVariables()
  const [saving, setSaving] = useState(false)

  const [editedAnnotation, setEditedAnnotation] = useState<KVAnnotation>(() => {
    return JSON.parse(JSON.stringify(selectedAnnotation))
  })

  useEffect(() => {
    setEditedAnnotation(JSON.parse(JSON.stringify(selectedAnnotation)))
  }, [selectedAnnotation])

  const onSave = async () => {
    setSaving(true)
    if (editedAnnotation.imageExport) {
      if (kestrelModel?.id) {
        const imageFilename = await findBestAnnotationImageFilename(
          selectedAnnotation,
          kestrelModel.id,
          authorizedAxios,
          KESTREL_API_URL
        )
        editedAnnotation.imageExportFilename = imageFilename
      }
    } else {
      editedAnnotation.imageExportFilename = null
    }

    updateAnnotation(
      selectedAnnotation,
      ['annotationLabel', 'annotationSeverity'],
      (a) => {
        a.label = editedAnnotation.label
        a.notes = editedAnnotation.notes
        a.severity = editedAnnotation.severity
        a.navyAnnotation = editedAnnotation.navyAnnotation
        a.rooftopAnnotation = editedAnnotation.rooftopAnnotation
        a.imageExport = editedAnnotation.imageExport
        a.imageExportFilename = editedAnnotation.imageExportFilename
        return a
      },
      [editedAnnotation.label, editedAnnotation.severity]
    )
    setSelectedMarkup(undefined)
    setSaving(false)
  }

  const onCancel = () => {
    setSelectedMarkup(undefined)
  }

  // Don't render if no popup position
  if (!popupMenuStartPosition) {
    return null
  }

  return (
    <Draggable
      style={{
        left: popupMenuStartPosition.x + 'px',
        top: popupMenuStartPosition.y + 'px',
        zIndex: 1000,
      }}>
      <PopupContainer>
        <DraggableHandle />
        <CloseButton
          setSelectedMarkup={setSelectedMarkup}
          style={{
            top: '8px',
            right: '10px',
          }}
        />

        {/* Label Row */}
        <InputGroup>
          <InputHeader>Label</InputHeader>
          <AnnotationLabelInput
            editedAnnotation={editedAnnotation}
            setEditedAnnotation={setEditedAnnotation}
          />
        </InputGroup>

        {/* Severity and Save Photo Row */}
        <SeveritySavePhotoRow>
          <SeveritySection>
            <InputHeader>Severity</InputHeader>
            <SeverityPicker
              editedAnnotation={editedAnnotation}
              setEditedAnnotation={setEditedAnnotation}
            />
          </SeveritySection>

          <SavePhotoSection>
            <InputHeader>Save Image</InputHeader>
            <CheckboxContainer>
              <SavePhotoCheckbox
                type='checkbox'
                checked={editedAnnotation.imageExport || false}
                onChange={(e) =>
                  setEditedAnnotation((prev) => ({
                    ...prev,
                    imageExport: e.target.checked,
                  }))
                }
              />
              {(editedAnnotation.imageExport || false) && (
                <CheckIcon
                  style={{
                    position: 'absolute',
                    color: 'white',
                    width: '16px',
                    height: '16px',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </CheckboxContainer>
          </SavePhotoSection>
        </SeveritySavePhotoRow>

        {/* Damage Type Row - only show if inspection type is defined */}
        {kestrelModel?.data.inspection_type && (
          <InputGroup>
            <InputHeader>Damage Type</InputHeader>
            <CenteredContent>
              <DamageTypeSelector
                editedAnnotation={editedAnnotation}
                setEditedAnnotation={setEditedAnnotation}
                inspectionType={kestrelModel.data.inspection_type}
              />
            </CenteredContent>
          </InputGroup>
        )}

        {/* Comments Row */}
        <InputGroup>
          <InputHeader>Comments</InputHeader>
          <AnnotationNotesInput
            editedAnnotation={editedAnnotation}
            setEditedAnnotation={setEditedAnnotation}
          />
        </InputGroup>

        {/* Save and Cancel Buttons Row */}
        <InputGroup>
          <CenteredContent>
            <ButtonRow>
              <Button onClick={onCancel}>Cancel</Button>
              <Button disabled={saving} onClick={onSave}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ButtonRow>
          </CenteredContent>
        </InputGroup>
      </PopupContainer>
    </Draggable>
  )
}
